import PropTypes from "prop-types";
import React from "react";
import { useHistory } from "react-router-dom";

// Level-agnostic header: the colour chip + title path on the left stay visually
// constant across drill-down levels. The current position is shown only as extra
// chevron segments appended to the title path (e.g. "Campaigns › Advanta 2020").
// Earlier segments with a `to` are clickable shortcuts back up. Filters live
// below the header, in the page body — not inside it.
// path: [{ label, to? }] — the last segment is the current page.
export const LevelHeader = ({ path, title, metrics, actions }) => {
  const history = useHistory();
  // A bare `title` string is treated as a single (current) path segment.
  const segments = path ?? (title != null ? [{ label: title }] : []);
  // Previous level = the parent breadcrumb segment (when there is one to go back to).
  const parent = segments.length > 1 ? segments[segments.length - 2] : null;

  return (
    <header className="cf-header">
      <div className="cf-header__main">
        <div className="cf-header__left">
          {parent?.to && (
            <button
              type="button"
              className="btn-icon btn-icon--outline cf-header__back"
              onClick={() => history.push(parent.to)}
              data-tooltip-id="tooltip"
              data-tooltip-content="Back"
              aria-label="Back"
            >
              <i className="fa-solid fa-arrow-left" aria-hidden="true" />
            </button>
          )}
          <h1 className="cf-header__title">
            <span className="cf-header__dot" aria-hidden="true" />
            {segments.map((segment, index) => {
              const isLast = index === segments.length - 1;
              const titleAttr =
                typeof segment.label === "string" ? segment.label : undefined;
              return (
                <React.Fragment key={index}>
                  {index > 0 && (
                    <i
                      className="fa-solid fa-chevron-right cf-header__sep"
                      aria-hidden="true"
                    />
                  )}
                  {isLast || !segment.to ? (
                    <span
                      className={
                        "cf-header__seg" + (isLast ? " is-current" : "")
                      }
                      title={titleAttr}
                    >
                      {segment.label}
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="cf-header__seg is-link"
                      onClick={() => history.push(segment.to)}
                      title={titleAttr}
                    >
                      {segment.label}
                    </button>
                  )}
                </React.Fragment>
              );
            })}
          </h1>
        </div>

        {/* Right group: KPIs, then the action buttons right beside them. */}
        {(metrics?.length > 0 || actions) && (
          <div className="cf-header__right">
            {metrics?.length > 0 && (
              <div className="cf-kpis">
                {metrics.map((metric) => (
                  <div className="cf-kpi" key={metric.label}>
                    <span className="cf-kpi__value">{metric.value}</span>
                    <span className="cf-kpi__label">{metric.label}</span>
                  </div>
                ))}
              </div>
            )}
            {actions && <div className="cf-header__actions">{actions}</div>}
          </div>
        )}
      </div>
    </header>
  );
};

LevelHeader.propTypes = {
  path: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node.isRequired,
      to: PropTypes.string,
    })
  ),
  title: PropTypes.node,
  metrics: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.node.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  actions: PropTypes.node,
};
